import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Dumbbell, Target, Star, Bookmark, Trophy, MessageSquare, Calendar, Instagram, Link2, ArrowLeft, Loader2, Lock, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-4 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="w-5 h-5 mx-auto mb-2" style={{ color }} />
      <p className="text-white text-2xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{value}</p>
      <p className="text-zinc-500 text-xs uppercase tracking-wider">{label}</p>
    </div>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'text-[#E63946] fill-[#E63946]' : 'text-zinc-700'}`} />
      ))}
    </div>
  );
}

export default function PublicProfilePage() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API}/api/profile/${userId}`);
        if (res.status === 403) { setError('private'); setLoading(false); return; }
        if (!res.ok) throw new Error('Profile not found');
        setProfile(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  useEffect(() => {
    if (!profile) return;
    // Load reviews and plans in parallel
    Promise.all([
      fetch(`${API}/api/profile/${userId}/reviews`).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/profile/${userId}/race-plans`).then(r => r.ok ? r.json() : []),
    ]).then(([revs, pls]) => {
      setReviews(revs);
      setPlans(Array.isArray(pls) ? pls : []);
    }).catch(() => {});
  }, [profile, userId]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-black pt-24 flex items-center justify-center" data-testid="public-profile-loading">
        <Loader2 className="w-8 h-8 text-[#e8ff00] animate-spin" />
      </main>
    );
  }

  if (error === 'private') {
    return (
      <main className="min-h-screen bg-black pt-24 flex flex-col items-center justify-center gap-4" data-testid="profile-private">
        <Lock className="w-12 h-12 text-zinc-700" />
        <h2 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Private Profile</h2>
        <p className="text-zinc-500 text-sm">This user has chosen to keep their profile private.</p>
        <Link to="/" className="text-[#e8ff00] text-sm hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Go Home
        </Link>
      </main>
    );
  }

  if (error || !profile) {
    return (
      <main className="min-h-screen bg-black pt-24 flex flex-col items-center justify-center gap-4" data-testid="profile-not-found">
        <User className="w-12 h-12 text-zinc-700" />
        <h2 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Profile Not Found</h2>
        <Link to="/" className="text-[#e8ff00] text-sm hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Go Home
        </Link>
      </main>
    );
  }

  const allReviews = [
    ...(reviews?.gym_reviews || []).map(r => ({ ...r, reviewType: 'gym' })),
    ...(reviews?.product_reviews || []).map(r => ({ ...r, reviewType: 'product' })),
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <main className="min-h-screen bg-black pt-24 pb-16 px-4" data-testid="public-profile-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 mb-6" data-testid="profile-header">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 flex items-center justify-center bg-zinc-800 border-2 border-[#e8ff00]/30 text-[#e8ff00] text-3xl font-bold flex-shrink-0" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                {(profile.first_name?.[0] || '?').toUpperCase()}{(profile.last_name?.[0] || '').toUpperCase()}
              </div>
              <div>
                <h1 className="text-white text-2xl md:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {profile.first_name} {profile.last_name}
                </h1>
                {profile.experience_level && (
                  <span className="text-zinc-500 text-xs uppercase tracking-wider">{profile.experience_level} Athlete</span>
                )}
                {profile.member_since && (
                  <p className="text-zinc-600 text-xs flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" /> Member since {new Date(profile.member_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
            <button onClick={handleShare} className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 text-zinc-400 text-xs hover:border-[#e8ff00] hover:text-[#e8ff00] transition-colors" data-testid="share-profile-btn">
              <Share2 className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Share'}
            </button>
          </div>

          {profile.bio && (
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed">{profile.bio}</p>
          )}

          {/* Social Links */}
          {profile.social_links && (profile.social_links.instagram || profile.social_links.strava) && (
            <div className="flex gap-3 mt-4">
              {profile.social_links.instagram && (
                <a href={`https://instagram.com/${profile.social_links.instagram}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors" data-testid="social-instagram">
                  <Instagram className="w-3.5 h-3.5" /> @{profile.social_links.instagram}
                </a>
              )}
              {profile.social_links.strava && (
                <a href={profile.social_links.strava.startsWith('http') ? profile.social_links.strava : `https://${profile.social_links.strava}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors" data-testid="social-strava">
                  <Link2 className="w-3.5 h-3.5" /> Strava
                </a>
              )}
            </div>
          )}

          {/* Fitness Goals */}
          {profile.fitness_goals && profile.fitness_goals.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {profile.fitness_goals.map(g => (
                <span key={g} className="text-[10px] uppercase tracking-wider px-2.5 py-1 border border-zinc-700 text-zinc-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Bookmark} label="Saved Items" value={profile.stats.saved_items} color="#e8ff00" />
          <StatCard icon={MessageSquare} label="Reviews" value={profile.stats.reviews_written + profile.stats.product_reviews} color="#E63946" />
          <StatCard icon={Trophy} label="Shared Plans" value={profile.stats.shared_plans} color="#06B6D4" />
          <StatCard icon={Target} label="Goals" value={profile.fitness_goals?.length || 0} color="#22c55e" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-800 mb-6">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'reviews', label: `Reviews (${allReviews.length})` },
            { id: 'plans', label: `Race Plans (${plans.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-xs uppercase tracking-wider transition-colors ${
                activeTab === t.id ? 'text-[#e8ff00] border-b-2 border-[#e8ff00]' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid={`profile-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4" data-testid="profile-overview">
            {allReviews.length === 0 && plans.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800">
                <Dumbbell className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">This athlete is just getting started!</p>
              </div>
            ) : (
              <>
                {allReviews.slice(0, 3).map((r) => (
                  <ReviewCard key={r.review_id || r.product_id} review={r} />
                ))}
                {plans.slice(0, 2).map((p) => (
                  <PlanCard key={p.plan_id} plan={p} />
                ))}
              </>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4" data-testid="profile-reviews">
            {allReviews.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No reviews yet</p>
            ) : allReviews.map((r) => (
              <ReviewCard key={r.review_id || r.product_id} review={r} />
            ))}
          </div>
        )}

        {/* Race Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-4" data-testid="profile-plans">
            {plans.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No shared race plans yet</p>
            ) : plans.map((p) => (
              <PlanCard key={p.plan_id} plan={p} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function ReviewCard({ review }) {
  const isGym = review.reviewType === 'gym';
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-4" data-testid={`review-card-${review.review_id || review.product_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} />
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-700 text-zinc-500" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {isGym ? 'Gym' : 'Product'}
          </span>
        </div>
        {review.created_at && (
          <span className="text-zinc-600 text-xs">{new Date(review.created_at).toLocaleDateString()}</span>
        )}
      </div>
      <p className="text-zinc-500 text-xs mb-1">
        {isGym ? `${review.gym_name} - ${review.gym_city}, ${review.gym_state}` : review.title || 'Product Review'}
      </p>
      <p className="text-zinc-300 text-sm leading-relaxed">{review.text || review.review_text}</p>
    </div>
  );
}

function PlanCard({ plan }) {
  return (
    <Link to={plan.share_token ? `/race-plan/${plan.share_token}` : '#'} className="block bg-zinc-900/60 border border-zinc-800 hover:border-[#e8ff00]/30 p-4 transition-colors" data-testid={`plan-card-${plan.plan_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-[#e8ff00]" />
            <span className="text-white text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              HYROX Training Plan
            </span>
          </div>
          <p className="text-zinc-500 text-xs">
            {plan.experience_level && <span className="capitalize">{plan.experience_level}</span>}
            {plan.race_date && <span> &middot; Race: {plan.race_date}</span>}
          </p>
        </div>
        {plan.created_at && (
          <span className="text-zinc-600 text-xs">{new Date(plan.created_at).toLocaleDateString()}</span>
        )}
      </div>
    </Link>
  );
}
